/* ═══════════════════════════════════════════════════════════════════════════
   THE DATA ROOM

   A searchable archive of everything in a case, and — the part that decides
   the whole shape — the rule about whose it is.

   ── Whose papers are they ─────────────────────────────────────────────────

   Chamcore's document centre shares a note by INSERTING A COPY of it against
   another user's id. That works, and for a sales team passing a lead around
   it is the obvious thing. It is the wrong thing here, and the reason is
   worth spelling out because everything below follows from it:

   if sharing copies, then a case handed from one lawyer to another leaves a
   copy behind and takes a copy along. The client now has their papers in two
   places, neither of which is theirs, and "the documents follow the client"
   is not what happened — the documents followed the lawyers, twice.

   So here the archive belongs to the CLIENT. A firm does not hold the papers;
   it holds ACCESS to them, for as long as it is engaged. Which means:

     sharing grants access — nothing is copied
     inviting co-counsel grants access — nothing is copied
     transferring the case moves the access, and the papers do not move,
       because they were never the firm's to move
     the client sees everything in their own room, always, including
       after a transfer, including to a firm they have left

   That last line is the one clients actually need and the one an inbox can
   never give them.

   ── The audit ────────────────────────────────────────────────────────────

   Every grant, revoke, transfer and send-out is recorded, and the client can
   read it. An archive that cannot say who has had sight of your divorce
   papers is a filing cabinet with the door off. Chamcore logs the same
   through `logSaleActivity`; the difference is who gets to read it.
   ═══════════════════════════════════════════════════════════════════════ */

/* ── What a paper is ──────────────────────────────────────────────────────
   Kinds, so the room can be filtered down to "just the contracts" without
   anybody typing a folder structure. Derived from the extension and from
   what the sender called it — never guessed from the contents, which this
   platform does not read. */
export const KINDS = [
  { id: 'contract', label: 'Contracts', hints: ['kontrakt', 'avtale', 'contract', 'oppdrag'] },
  { id: 'extract', label: 'Extracts', hints: ['utdrag', 'extract', 'utskrift'] },
  { id: 'ruling', label: 'Rulings', hints: ['dom', 'kjennelse', 'vedtak', 'ruling'] },
  { id: 'letter', label: 'Letters', hints: ['brev', 'letter', 'varsel', 'krav'] },
  { id: 'receipt', label: 'Receipts', hints: ['kvittering', 'faktura', 'receipt', 'invoice'] },
  { id: 'image', label: 'Images', hints: [] },
  { id: 'other', label: 'Other', hints: [] },
];

const IMAGE = ['png', 'jpg', 'jpeg', 'heic', 'webp', 'gif', 'tif', 'tiff'];

/** The kind of a paper, from its name. A guess, and labelled as one on the
    screen — the sender can say otherwise and that wins. */
export function kindOf({ name = '', kind } = {}) {
  if (kind && KINDS.some(k => k.id === kind)) return kind;
  const lower = String(name).toLowerCase();
  const ext = (lower.split('.').pop() || '');
  if (IMAGE.includes(ext)) return 'image';
  const hit = KINDS.find(k => k.hints.some(h => lower.includes(h)));
  return hit ? hit.id : 'other';
}

export const kindLabel = id =>
  (KINDS.find(k => k.id === id) || KINDS[KINDS.length - 1]).label;

/* ── Who may see what ─────────────────────────────────────────────────────

   A document carries `access`: a list of `{ who, role, from, until }`.
   `who` is a business id or a person id; `until` is null while it lasts.

   The client is not in the list. They do not need to be — it is their room,
   and a rule that could take a client out of their own archive is a rule
   somebody will eventually apply by accident. */

export const ROLES = {
  engaged: 'The firm you engaged',
  cocounsel: 'Invited to work on this with them',
  former: 'Had access until the case moved',
};

/** Is this grant live at this moment? */
const live = (g, now) => !g.until || Date.parse(g.until) > now.getTime();

/**
 * May this viewer see this document?
 *
 * `viewer` is `{ kind: 'client' | 'business' | 'person', id }`.
 * A client sees their own room. Everybody else needs a live grant.
 */
export function canSee(doc, viewer, now = new Date()) {
  if (!doc || !viewer) return false;
  if (viewer.kind === 'client') return true;
  return (doc.access || []).some(g => g.who === viewer.id && live(g, now));
}

/** Everybody who can see it right now, for the line that says so. */
export const seenBy = (doc, now = new Date()) =>
  (doc.access || []).filter(g => live(g, now));

/** And everybody who could, once. The client is entitled to this. */
export const everSeenBy = doc => (doc.access || []);

/**
 * Grant access, without copying anything.
 *
 * Returns a new access list. Granting twice does not stack — it reopens the
 * existing grant, so a firm that was given access, lost it and was given it
 * again has one row with one history rather than three rows saying different
 * things about the same relationship.
 */
export function grant(access = [], who, role = 'cocounsel', at = new Date(), extra = {}) {
  const had = access.find(g => g.who === who);
  if (had) {
    return access.map(g => (g.who === who
      ? { ...g, ...extra, role, until: null, from: g.from || at.toISOString() }
      : g));
  }
  return [...access, { who, role, from: at.toISOString(), until: null, ...extra }];
}

/** Take it away. The row stays, closed — the client is entitled to know that
    somebody once had sight of this, and deleting the row would be deleting
    the answer. */
export function revoke(access = [], who, at = new Date()) {
  return (access || []).map(g => (g.who === who && !g.until
    ? { ...g, until: at.toISOString(), role: 'former' }
    : g));
}

/**
 * Move the case to another firm.
 *
 * The outgoing firm's access closes and the incoming firm's opens. Nothing is
 * copied and nothing is deleted, because the papers are the client's and were
 * never the firm's to move — which is the whole point of the model and the
 * thing that makes the promise "the documents follow the client" true rather
 * than a slogan.
 */
export function transfer(access = [], from, to, at = new Date()) {
  const closed = revoke(access, from, at);
  return grant(closed, to, 'engaged', at);
}

/* ── Finding things ───────────────────────────────────────────────────────
   The reason a data room exists rather than a folder. Everything is one
   query: words, kind, who sent it, and when. */

const foldish = s => String(s || '').toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * @param {object[]} docs
 * @param {object} by  { q, kind, from ('them'|'you'), since, until }
 */
export function find(docs = [], by = {}) {
  const terms = foldish(by.q).split(/\s+/).filter(Boolean);
  return docs.filter(d => {
    if (by.kind && by.kind !== 'all' && kindOf(d) !== by.kind) return false;
    if (by.from && by.from !== 'all') {
      const mine = d.by === 'customer';
      if (by.from === 'you' && !mine) return false;
      if (by.from === 'them' && mine) return false;
    }
    if (by.since && String(d.at) < by.since) return false;
    if (by.until && String(d.at) > by.until) return false;
    if (!terms.length) return true;
    /* The note that came with it is searched too. A room where the file name
       is the only handle is a room where "the one about the boundary" cannot
       be found, and that is how people actually remember documents. */
    const hay = foldish([d.name, d.note, kindLabel(kindOf(d))].filter(Boolean).join(' '));
    return terms.every(t => hay.includes(t));
  });
}

/** How many of each kind are in here, for the filter chips. A chip that leads
    to an empty room is a chip worth not drawing. */
export function counts(docs = []) {
  const out = { all: docs.length };
  docs.forEach(d => { const k = kindOf(d); out[k] = (out[k] || 0) + 1; });
  return out;
}

/** Bytes, in the shape a person reads. */
export const sizeWords = n => (n >= 1024 * 1024
  ? (n / 1024 / 1024).toFixed(1) + ' MB'
  : Math.max(1, Math.round((n || 0) / 1024)) + ' KB');
